/**
 * Roles accordion section
 *
 * Houses the 5 role checkboxes, plus two role-gated sub-blocks:
 *   - Co-sign requirement (shows when Clinician is selected)
 *   - Prescribing proxy (shows when Operations Support is selected, GDN-1119)
 */

import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import type { ProviderRoleInfo } from '@store/generated/organizations';
import { getRoleDisplayName, ROLES } from '@utils/roleUtils';

interface RolesSectionProps {
  rolesToDisplay: ProviderRoleInfo[];
  selectedRoles: Set<string>;
  onToggleRole: (role: string) => void;
  needsCosign: boolean;
  onNeedsCosignChange: (value: boolean) => void;
  canPrepareOnBehalf: boolean;
  onCanPrepareOnBehalfChange: (value: boolean) => void;
  disabled?: boolean;
}

export function RolesSection({
  rolesToDisplay,
  selectedRoles,
  onToggleRole,
  needsCosign,
  onNeedsCosignChange,
  canPrepareOnBehalf,
  onCanPrepareOnBehalfChange,
  disabled = false,
}: RolesSectionProps) {
  const isClinician = selectedRoles.has(ROLES.CLINICIAN);
  const isOperationsSupport = selectedRoles.has(ROLES.OPERATIONS_SUPPORT);

  return (
    <Stack spacing={2}>
      <FormGroup>
        {rolesToDisplay.map(role => (
          <FormControlLabel
            key={role.role}
            control={
              <Checkbox
                checked={selectedRoles.has(role.role)}
                onChange={() => onToggleRole(role.role)}
                disabled={disabled}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {role.display_name || getRoleDisplayName(role.role)}
                </Typography>
                {role.description && (
                  <Typography variant="caption" color="text.secondary">
                    {role.description}
                  </Typography>
                )}
              </Box>
            }
            sx={{ alignItems: 'flex-start', py: 0.5 }}
          />
        ))}
      </FormGroup>

      {/*
        Co-sign placement: stays grouped with Clinician (matches existing
        EditMemberDialog semantic — needs_cosign applies to clinicians whose
        notes a supervisor must sign off on).
      */}
      {isClinician && (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Supervision
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={needsCosign}
                  onChange={e => onNeedsCosignChange(e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Require co-signature</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Notes must be co-signed by a supervisor before being finalized
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        </>
      )}

      {/* GDN-1119: per-person prescribing-proxy capability, ops-support only */}
      {isOperationsSupport && (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Prescribing
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={canPrepareOnBehalf}
                  onChange={e => onCanPrepareOnBehalfChange(e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Prescribing Proxy</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Prep prescriptions on behalf of a prescriber. Requires JotRx on the account.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        </>
      )}

    </Stack>
  );
}
